import {
  SET_SID_SESSIONS,
  SET_SID_SELECTED_SESSION,
  SET_SID_MESSAGES,
  PREPEND_SID_MESSAGES,
  ADD_SID_MESSAGE,
  SET_SID_THINKING,
  SET_SID_LOADING_OLDER,
  EDIT_SID_MESSAGE,
  DROP_LAST_SID_REPLY,
  SET_SID_MESSAGE_FEEDBACK,
  SET_SID_INPUT_DRAFT,
  CLEAR_SID_INPUT_DRAFT,
} from "../constants/sidConst";

const initialState = {
  sessions: [],
  selectedSessionId: null, // null = new chat (session created on first message)
  messages: [], // oldest-first; older pages prepend, new messages append
  isThinking: false,
  hasMoreMessages: false, // an older page exists for scroll-up loading
  oldestMessageId: null, // nextCursor — the _id to fetch older messages before
  isLoadingOlder: false,
  inputDraft: "", // suggestion-chip pre-fill (cleared once the field adopts it)
};

export default function sidReducer(state = initialState, action) {
  switch (action.type) {
    case SET_SID_SESSIONS:
      return { ...state, sessions: action.payload || [] };
    case SET_SID_SELECTED_SESSION:
      return {
        ...state,
        selectedSessionId: action.payload,
        messages: [],
        isThinking: false,
        hasMoreMessages: false,
        oldestMessageId: null,
        isLoadingOlder: false,
        inputDraft: "", // a stale chip draft must not leak into another chat
      };
    case SET_SID_MESSAGES: {
      // Initial page load. Accepts the paginated shape
      // { messages, nextCursor, hasMore } or a plain message array.
      const payload = Array.isArray(action.payload)
        ? { messages: action.payload }
        : action.payload || {};
      return {
        ...state,
        messages: payload.messages || [],
        hasMoreMessages: Boolean(payload.hasMore),
        oldestMessageId: payload.nextCursor || null,
      };
    }
    case PREPEND_SID_MESSAGES: {
      // Older page from scroll-up. The keyset cursor guarantees no overlap,
      // but dedupe by _id anyway so a replayed fetch can't double-render.
      const older = action.payload?.messages || [];
      const existingIds = new Set(state.messages.map((m) => m._id));
      const fresh = older.filter((m) => !existingIds.has(m._id));
      return {
        ...state,
        messages: [...fresh, ...state.messages],
        hasMoreMessages: Boolean(action.payload?.hasMore),
        oldestMessageId: action.payload?.nextCursor || null,
      };
    }
    case ADD_SID_MESSAGE:
      return {
        ...state,
        messages: [...state.messages, action.payload],
        isThinking:
          action.payload?.role === "assistant" ? false : state.isThinking,
      };
    case SET_SID_THINKING:
      return { ...state, isThinking: action.payload };
    case SET_SID_LOADING_OLDER:
      return { ...state, isLoadingOlder: Boolean(action.payload) };
    case EDIT_SID_MESSAGE: {
      // ChatGPT-style edit: rewrite the text AND truncate everything after —
      // the re-asked reply arrives via ADD_SID_MESSAGE. Pagination cursor is
      // untouched (only messages BELOW this one are removed).
      const { message_id, text } = action.payload || {};
      const index = state.messages.findIndex((m) => m._id === message_id);
      if (index === -1) return state;
      return {
        ...state,
        messages: [
          ...state.messages.slice(0, index),
          { ...state.messages[index], text },
        ],
      };
    }
    case DROP_LAST_SID_REPLY: {
      // Regenerate prep: remove trailing assistant messages so the thread
      // ends on the user message being re-asked.
      const messages = [...state.messages];
      while (messages.length && messages[messages.length - 1].role === "assistant") {
        messages.pop();
      }
      return { ...state, messages };
    }
    case SET_SID_MESSAGE_FEEDBACK: {
      const { message_id, rating } = action.payload || {};
      return {
        ...state,
        messages: state.messages.map((m) =>
          m._id === message_id ? { ...m, feedback: rating } : m
        ),
      };
    }
    case SET_SID_INPUT_DRAFT:
      return { ...state, inputDraft: action.payload || "" };
    case CLEAR_SID_INPUT_DRAFT:
      return { ...state, inputDraft: "" };
    default:
      return state;
  }
}
